import { useEffect, useState } from 'react'
import { api, ApiRequestError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useAsync } from '../lib/useAsync'
import { CardSkeleton, ErrorState, PageHeader, Section } from '../components/ui'
import './Settings.css'

/**
 * Runtime thresholds, reachable from screen 7f's "Şərtləri dəyiş".
 *
 * These live in the database rather than in code precisely so they can be tuned
 * without a deploy — moderator eligibility above all, which the design lets an
 * admin change from the applications screen.
 */

const GROUPS: { title: string; note?: string; keys: Record<string, string> }[] = [
  {
    title: 'Moderator şərtləri',
    note: 'Screen 7f — müraciətlər bu hədlərə görə qiymətləndirilir.',
    keys: {
      'moderator.min_stars': 'Minimum ulduz',
      'moderator.min_account_age_days': 'Minimum hesab yaşı (gün)',
      'moderator.max_violations': 'İcazə verilən pozuntu',
      'moderator.min_accuracy_pct': 'Minimum şikayət dəqiqliyi (%)',
      'moderator.suspend_after_wrong': 'Neçə səhv qərardan sonra dayandırılsın',
    },
  },
  {
    title: 'Ulduz hədləri',
    note: 'Dəvət limitsizdir; «Dəvət üçün ulduz» 0-dırsa qapı yoxdur.',
    keys: {
      'share.min_stars': 'Paylaşma üçün ulduz',
      'invite.min_stars': 'Dəvət üçün ulduz',
    },
  },
  {
    title: 'Dəvət bonusu',
    note: 'Hər qəbul olunan dəvətə görə bir dəfə ödənilir. Dəyişiklik yalnız yeni dəvətlərə təsir edir.',
    keys: {
      'invite.reward_inviter': 'Dəvət edənə ulduz',
      'invite.reward_invitee': 'Dəvət olunana ulduz',
    },
  },
  {
    title: 'Giriş təhlükəsizliyi',
    keys: {
      'auth.code_ttl_seconds': 'Kodun etibarlılıq müddəti (saniyə)',
      'auth.code_max_attempts': 'İcazə verilən cəhd sayı',
      'auth.lock_minutes': 'Kilid müddəti (dəqiqə)',
    },
  },
  {
    title: 'Lent və saxlama',
    keys: {
      'feed.hot_min_votes': '“Populyar” üçün minimum səs',
      'account.purge_grace_days': 'Hesab silmə güzəşti (gün)',
      'journal.retention_months': 'Jurnal saxlama müddəti (ay)',
    },
  },
]

export function Settings() {
  const { can } = useAuth()
  const editable = can('SUPER_ADMIN')

  const { data, error, loading, reload } = useAsync(() => api.settings(), [])
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) setDraft(Object.fromEntries(data.map((s) => [s.key, s.value])))
  }, [data])

  const changed = data?.filter((s) => draft[s.key] !== undefined && draft[s.key] !== s.value) ?? []

  async function save() {
    setSaving(true)
    setSaveError(null)
    setFieldErrors({})
    setSaved(false)

    try {
      await api.updateSettings(
        Object.fromEntries(changed.map((s) => [s.key, draft[s.key] ?? s.value])),
      )
      setSaved(true)
      reload()
    } catch (cause) {
      if (cause instanceof ApiRequestError) {
        setSaveError(cause.message)
        setFieldErrors(cause.details)
      } else {
        setSaveError('Parametrlər yadda saxlanmadı.')
      }
    } finally {
      setSaving(false)
    }
  }

  if (error) return <ErrorState error={error} onRetry={reload} />
  if (loading || !data) return <CardSkeleton height={420} />

  const known = new Set(GROUPS.flatMap((g) => Object.keys(g.keys)))
  const others = data.filter((s) => !known.has(s.key))

  return (
    <>
      <PageHeader
        title="Parametrlər"
        subtitle={
          editable
            ? 'Dəyişikliklər dərhal qüvvəyə minir və jurnala yazılır.'
            : 'Yalnız baş admin dəyişə bilər.'
        }
        actions={
          editable && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={saving || changed.length === 0}
              onClick={() => void save()}
            >
              {saving
                ? 'Yadda saxlanılır…'
                : changed.length > 0
                  ? `${changed.length} dəyişikliyi saxla`
                  : 'Dəyişiklik yoxdur'}
            </button>
          )
        }
      />

      {saveError && (
        <p className="settings__error" role="alert">
          {saveError}
        </p>
      )}
      {saved && changed.length === 0 && (
        <p className="settings__saved" role="status">
          Parametrlər yeniləndi.
        </p>
      )}

      {GROUPS.map((group) => (
        <Section key={group.title} title={group.title}>
          {group.note && <p className="muted settings__note">{group.note}</p>}

          <div className="settings__grid">
            {Object.entries(group.keys).map(([key, label]) => {
              const setting = data.find((s) => s.key === key)
              if (!setting) return null

              return (
                <label key={key} className="settings__field">
                  <span className="settings__label">{label}</span>
                  <input
                    className="input settings__input"
                    inputMode="numeric"
                    value={draft[key] ?? setting.value}
                    disabled={!editable}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  />
                  <span className="settings__key">{key}</span>
                  {fieldErrors[key] && <span className="field-error">{fieldErrors[key]}</span>}
                </label>
              )
            })}
          </div>
        </Section>
      ))}

      {others.length > 0 && (
        <Section title="Digər">
          <div className="settings__grid">
            {others.map((setting) => (
              <label key={setting.key} className="settings__field">
                <span className="settings__label">{setting.key}</span>
                <input
                  className="input settings__input"
                  value={draft[setting.key] ?? setting.value}
                  disabled={!editable}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [setting.key]: e.target.value }))
                  }
                />
                {fieldErrors[setting.key] && (
                  <span className="field-error">{fieldErrors[setting.key]}</span>
                )}
              </label>
            ))}
          </div>
        </Section>
      )}
    </>
  )
}
